import { useCallback, useEffect, useRef } from "react";
import { FloatingBall as VantFloatingBall } from "react-vant";

const DRAG_CLICK_DISTANCE = 5;
const SUPPRESS_CLICK_AFTER_DRAG_MS = 350;
const TRANSITION_DURATION = 300;

function toPx(value, fallback = 0) {
  if (value == null) {
    return fallback;
  }

  if (typeof value === "number") {
    return value;
  }

  const str = String(value).trim();
  const num = parseFloat(str);
  if (Number.isNaN(num)) {
    return fallback;
  }

  if (str.endsWith("rem")) {
    const fontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    return num * (fontSize || 16);
  }

  if (str.endsWith("vw")) {
    return (num * window.innerWidth) / 100;
  }

  if (str.endsWith("vh")) {
    return (num * window.innerHeight) / 100;
  }

  return num;
}

function getAppBounds() {
  const app = document.getElementById("app") || document.getElementById("preact_root");
  const rect = app && app.getBoundingClientRect();

  if (rect && rect.width && rect.height) {
    return rect;
  }

  return {
    left: 0,
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    width: window.innerWidth,
    height: window.innerHeight
  };
}

function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}

function getTranslate(element) {
  const transform = window.getComputedStyle(element).transform;

  if (!transform || transform === "none") {
    return { x: 0, y: 0 };
  }

  if (window.DOMMatrixReadOnly) {
    const matrix = new DOMMatrixReadOnly(transform);
    return { x: matrix.m41, y: matrix.m42 };
  }

  const matrix = transform.match(/^matrix\((.+)\)$/);
  if (matrix) {
    const values = matrix[1].split(",").map((value) => parseFloat(value.trim()));
    return { x: values[4] || 0, y: values[5] || 0 };
  }

  const matrix3d = transform.match(/^matrix3d\((.+)\)$/);
  if (matrix3d) {
    const values = matrix3d[1].split(",").map((value) => parseFloat(value.trim()));
    return { x: values[12] || 0, y: values[13] || 0 };
  }

  return { x: 0, y: 0 };
}

function getAdsorbDistance(adsorb) {
  if (!adsorb) {
    return null;
  }

  if (adsorb === true) {
    return 0;
  }

  return toPx(adsorb.distance, 0);
}

function setTransform(element, translate) {
  element.style.transform = `translate3d(${translate.x}px, ${translate.y}px, 0)`;
}

function isInteractiveTarget(target, root) {
  if (!target || !root || !target.closest) {
    return false;
  }

  const interactive = target.closest("a,button,input,textarea,select,label,[data-floating-ball-no-drag]");
  return Boolean(interactive && root.contains(interactive));
}

const DraggableFloatingBall = ({ adsorb, draggable = true, disabled, children, style, offset, onClick, ...props }) => {
  const wrapperRef = useRef(null);
  const dragRef = useRef(null);
  const cleanupDragRef = useRef(null);
  const adsorbRef = useRef(adsorb);
  const suppressClickRef = useRef(false);
  const suppressClickUntilRef = useRef(0);
  const suppressClickTimerRef = useRef(null);
  const touchStartTimeRef = useRef(0);

  useEffect(() => {
    adsorbRef.current = adsorb;
  }, [adsorb]);

  const getBallElement = useCallback(() => {
    return wrapperRef.current && wrapperRef.current.querySelector(".rv-floating-ball");
  }, []);

  const cleanupDrag = useCallback(() => {
    if (cleanupDragRef.current) {
      cleanupDragRef.current();
      cleanupDragRef.current = null;
    }
  }, []);

  const clearSuppressClick = useCallback(() => {
    suppressClickRef.current = false;
    suppressClickUntilRef.current = 0;
    if (suppressClickTimerRef.current) {
      clearTimeout(suppressClickTimerRef.current);
      suppressClickTimerRef.current = null;
    }
  }, []);

  const suppressNextDragClick = useCallback(() => {
    clearSuppressClick();
    suppressClickRef.current = true;
    suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_AFTER_DRAG_MS;
    suppressClickTimerRef.current = window.setTimeout(clearSuppressClick, SUPPRESS_CLICK_AFTER_DRAG_MS);
  }, [clearSuppressClick]);

  const adsorbToSide = useCallback(
    (element = getBallElement()) => {
      const distance = getAdsorbDistance(adsorbRef.current);
      if (!element || distance == null) {
        return;
      }

      const bounds = getAppBounds();
      const rect = element.getBoundingClientRect();
      const translate = getTranslate(element);
      const isLeftSide = rect.left + rect.width / 2 < bounds.left + bounds.width / 2;
      const targetLeft = isLeftSide ? bounds.left + distance : bounds.right - rect.width - distance;

      element.style.transitionDuration = `${TRANSITION_DURATION}ms`;
      setTransform(element, {
        x: translate.x + targetLeft - rect.left,
        y: translate.y
      });

      window.setTimeout(() => {
        element.style.transitionDuration = "";
      }, TRANSITION_DURATION);
    },
    [getBallElement]
  );

  const finishDrag = useCallback(
    (shouldAdsorb = true) => {
      const drag = dragRef.current;
      dragRef.current = null;
      cleanupDrag();

      if (!drag) {
        return;
      }

      if (drag.pointerId != null && drag.element.releasePointerCapture) {
        try {
          if (!drag.element.hasPointerCapture || drag.element.hasPointerCapture(drag.pointerId)) {
            drag.element.releasePointerCapture(drag.pointerId);
          }
        } catch (error) {
          console.warn("releasePointerCapture failed", error);
        }
      }

      const distance = Math.max(Math.abs(drag.lastX - drag.startClientX), Math.abs(drag.lastY - drag.startClientY));
      const isDrag = distance >= DRAG_CLICK_DISTANCE;
      if (isDrag) {
        suppressNextDragClick();
      }

      if (shouldAdsorb && isDrag) {
        adsorbToSide(drag.element);
      }
    },
    [adsorbToSide, cleanupDrag, suppressNextDragClick]
  );

  const moveDrag = useCallback(
    (clientX, clientY, event) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }

      if (event) {
        if (event.cancelable !== false) {
          event.preventDefault();
        }
        if (event.stopPropagation) {
          event.stopPropagation();
        }
      }

      drag.lastX = clientX;
      drag.lastY = clientY;

      const bounds = getAppBounds();
      const nextLeft = clamp(
        drag.startRect.left + clientX - drag.startClientX,
        bounds.left,
        bounds.right - drag.startRect.width
      );
      const nextTop = clamp(
        drag.startRect.top + clientY - drag.startClientY,
        bounds.top,
        bounds.bottom - drag.startRect.height
      );

      setTransform(drag.element, {
        x: drag.startTranslate.x + nextLeft - drag.startRect.left,
        y: drag.startTranslate.y + nextTop - drag.startRect.top
      });
    },
    []
  );

  const beginDrag = useCallback(
    (element, clientX, clientY, pointerId) => {
      cleanupDrag();
      element.style.transitionDuration = "0ms";
      dragRef.current = {
        element,
        pointerId,
        startClientX: clientX,
        startClientY: clientY,
        lastX: clientX,
        lastY: clientY,
        startRect: element.getBoundingClientRect(),
        startTranslate: getTranslate(element)
      };
    },
    [cleanupDrag]
  );

  const handlePointerDown = useCallback(
    (event) => {
      if (!window.PointerEvent || !draggable || disabled) {
        return;
      }

      // Some older Android WebViews expose PointerEvent but need the touch fallback.
      if (event.pointerType === "touch" && Date.now() - touchStartTimeRef.current < 500) {
        return;
      }

      if ((event.pointerType === "mouse" || !event.pointerType) && event.button !== 0) {
        return;
      }

      const element = getBallElement();
      if (!element || !element.contains(event.target)) {
        return;
      }

      if (isInteractiveTarget(event.target, element)) {
        return;
      }

      event.stopPropagation();
      beginDrag(element, event.clientX, event.clientY, event.pointerId);

      const pointerId = event.pointerId;
      if (pointerId != null && element.setPointerCapture) {
        try {
          element.setPointerCapture(pointerId);
        } catch (error) {
          console.warn("setPointerCapture failed", error);
        }
      }

      const handlePointerMove = (moveEvent) => {
        if (moveEvent.pointerId !== pointerId) {
          return;
        }

        if (moveEvent.pointerType === "mouse" && moveEvent.buttons != null && (moveEvent.buttons & 1) !== 1) {
          finishDrag();
          return;
        }

        moveDrag(moveEvent.clientX, moveEvent.clientY, moveEvent);
      };
      const handlePointerEnd = (endEvent) => {
        if (endEvent.pointerId === pointerId) {
          finishDrag();
        }
      };
      const handleBlur = () => finishDrag(false);

      element.addEventListener("pointermove", handlePointerMove, { passive: false });
      element.addEventListener("pointerup", handlePointerEnd, true);
      element.addEventListener("pointercancel", handlePointerEnd, true);
      window.addEventListener("pointermove", handlePointerMove, { passive: false });
      window.addEventListener("pointerup", handlePointerEnd, true);
      window.addEventListener("pointercancel", handlePointerEnd, true);
      window.addEventListener("blur", handleBlur, true);

      cleanupDragRef.current = () => {
        element.removeEventListener("pointermove", handlePointerMove);
        element.removeEventListener("pointerup", handlePointerEnd, true);
        element.removeEventListener("pointercancel", handlePointerEnd, true);
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerEnd, true);
        window.removeEventListener("pointercancel", handlePointerEnd, true);
        window.removeEventListener("blur", handleBlur, true);
      };

    },
    [beginDrag, disabled, draggable, finishDrag, getBallElement, moveDrag]
  );

  const handleMouseDown = useCallback(
    (event) => {
      if (window.PointerEvent || !draggable || disabled || event.button !== 0) {
        return;
      }

      const element = getBallElement();
      if (!element || !element.contains(event.target)) {
        return;
      }

      if (isInteractiveTarget(event.target, element)) {
        return;
      }

      event.stopPropagation();
      beginDrag(element, event.clientX, event.clientY);

      const handleMouseMove = (moveEvent) => {
        if (moveEvent.buttons != null && (moveEvent.buttons & 1) !== 1) {
          finishDrag();
          return;
        }

        moveDrag(moveEvent.clientX, moveEvent.clientY, moveEvent);
      };
      const handleMouseEnd = () => finishDrag();
      const handleBlur = () => finishDrag(false);

      window.addEventListener("mousemove", handleMouseMove, { passive: false });
      window.addEventListener("mouseup", handleMouseEnd, true);
      window.addEventListener("dragend", handleMouseEnd, true);
      window.addEventListener("blur", handleBlur, true);

      cleanupDragRef.current = () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseEnd, true);
        window.removeEventListener("dragend", handleMouseEnd, true);
        window.removeEventListener("blur", handleBlur, true);
      };
    },
    [beginDrag, disabled, draggable, finishDrag, getBallElement, moveDrag]
  );

  const handleTouchStart = useCallback(
    (event) => {
      if (!draggable || disabled || event.touches.length !== 1) {
        return;
      }

      const element = getBallElement();
      if (!element || !element.contains(event.target)) {
        return;
      }

      if (isInteractiveTarget(event.target, element)) {
        return;
      }

      const touch = event.touches[0];
      touchStartTimeRef.current = Date.now();
      event.stopPropagation();
      beginDrag(element, touch.clientX, touch.clientY);

      const handleTouchMove = (moveEvent) => {
        if (moveEvent.touches.length !== 1) {
          finishDrag();
          return;
        }

        moveDrag(moveEvent.touches[0].clientX, moveEvent.touches[0].clientY, moveEvent);
      };
      const handleTouchEnd = () => finishDrag();
      const handleBlur = () => finishDrag(false);

      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd, true);
      window.addEventListener("touchcancel", handleTouchEnd, true);
      window.addEventListener("blur", handleBlur, true);

      cleanupDragRef.current = () => {
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd, true);
        window.removeEventListener("touchcancel", handleTouchEnd, true);
        window.removeEventListener("blur", handleBlur, true);
      };
    },
    [beginDrag, disabled, draggable, finishDrag, getBallElement, moveDrag]
  );

  const handleClickCapture = useCallback((event) => {
    if (!suppressClickRef.current) {
      return;
    }

    if (Date.now() > suppressClickUntilRef.current) {
      clearSuppressClick();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
      event.nativeEvent.stopImmediatePropagation();
    }
    clearSuppressClick();
  }, [clearSuppressClick]);

  const handleClick = useCallback(
    (event) => {
      if (typeof onClick === "function") {
        onClick(event);
      }
    },
    [onClick]
  );

  useEffect(() => {
    return () => {
      cleanupDrag();
      clearSuppressClick();
    };
  }, [cleanupDrag, clearSuppressClick]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    wrapper.addEventListener("touchstart", handleTouchStart, { capture: true, passive: false });
    wrapper.addEventListener("pointerdown", handlePointerDown, true);
    wrapper.addEventListener("mousedown", handleMouseDown, true);

    return () => {
      wrapper.removeEventListener("touchstart", handleTouchStart, true);
      wrapper.removeEventListener("pointerdown", handlePointerDown, true);
      wrapper.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [handleMouseDown, handlePointerDown, handleTouchStart]);

  return (
    <div
      class="app-floating-ball-wrapper"
      ref={wrapperRef}
      onClickCapture={handleClickCapture}
      onClick={handleClick}
      onDragStart={(event) => event.preventDefault()}
      style={{
        touchAction: "none",
        WebkitUserDrag: "none",
        WebkitUserSelect: "none",
        userSelect: "none"
      }}
    >
      <VantFloatingBall
        adsorb={false}
        draggable={false}
        disabled={disabled}
        offset={offset}
        style={Object.assign({ touchAction: "none", WebkitUserDrag: "none", userSelect: "none", WebkitUserSelect: "none" }, style)}
        {...props}
      >
        {children}
      </VantFloatingBall>
    </div>
  );
};

export default DraggableFloatingBall;
